/** DataGrid — TanStack Table (headless) + CSS Grid.
 *  grid-template-columns dibangun dari hint lebar per kolom:
 *    "max"           → max-content        (pas konten: tanggal, angka, badge)
 *    "min:200"       → minmax(200px, 1fr) (fleksibel dgn lantai)
 *    "fr" / "fr:2"   → 1fr / 2fr          (pengisi ruang: judul, teks panjang)
 *  Sorting: mode "client" (TanStack getSortedRowModel) atau "server" (callback onSort). */
import { ReactNode, useMemo, useState } from "react";
import {
  ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";

export interface GridCol<T> {
  id: string;
  header: ReactNode;
  width?: string;              // "max" | "min:NNN" | "fr" | "fr:N"  (default "max")
  sortable?: boolean;
  accessor?: (row: T) => unknown; // utk sort client
  cell: (row: T) => ReactNode;
  align?: "left" | "right" | "center";
}

function widthToTemplate(w?: string): string {
  if (!w || w === "max") return "max-content";
  if (w.startsWith("min:")) return `minmax(${w.slice(4)}px, 1fr)`;
  if (w.startsWith("fr:")) return `${w.slice(3)}fr`;
  if (w === "fr") return "1fr";
  return w; // escape hatch: nilai CSS mentah
}

export function DataGrid<T>({ cols, data, sortMode = "client", serverSort, onServerSort, onRowClick, expandedRender, emptyText = "Tidak ada data untuk filter ini.", minTableWidth }: {
  cols: GridCol<T>[];
  data: T[];
  sortMode?: "client" | "server";
  serverSort?: { key: string; dir: "asc" | "desc" } | null;
  onServerSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
  expandedRender?: (row: T) => ReactNode | null;
  emptyText?: string;
  minTableWidth?: number;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo<ColumnDef<T>[]>(() => cols.map((c) => ({
    id: c.id,
    header: () => c.header,
    accessorFn: c.accessor ?? (() => null),
    enableSorting: sortMode === "client" && !!c.sortable,
    cell: (info) => c.cell(info.row.original),
  })), [cols, sortMode]);

  const table = useReactTable({
    data, columns,
    state: sortMode === "client" ? { sorting } : {},
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    ...(sortMode === "client" ? { getSortedRowModel: getSortedRowModel() } : {}),
  });

  const [hoverRow, setHoverRow] = useState<string | null>(null);
  const template = cols.map((c) => widthToTemplate(c.width)).join(" ");
  const rows = table.getRowModel().rows;

  function headerClick(c: GridCol<T>) {
    if (!c.sortable) return;
    if (sortMode === "server") { onServerSort?.(c.id); return; }
    const cur = sorting[0];
    setSorting(cur?.id === c.id ? [{ id: c.id, desc: !cur.desc }] : [{ id: c.id, desc: false }]);
  }
  function arrow(c: GridCol<T>): string {
    if (!c.sortable) return "";
    if (sortMode === "server") return serverSort?.key === c.id ? (serverSort.dir === "asc" ? " \u25B2" : " \u25BC") : "";
    const cur = sorting[0];
    return cur?.id === c.id ? (cur.desc ? " \u25BC" : " \u25B2") : "";
  }

  return (
    <div className="avio-scroll" style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: template, minWidth: minTableWidth, fontSize: "var(--text-base)" }}>
        {cols.map((c) => (
          <div key={c.id} onClick={() => headerClick(c)} role="columnheader"
            style={{ padding: "var(--space-5) var(--cell-px)", borderBottom: "1px solid var(--color-border)", background: "var(--color-surface-sunken)",
              fontWeight: "var(--weight-semi)" as any, fontSize: "var(--text-xs)", letterSpacing: ".06em", textTransform: "uppercase",
              color: "var(--color-text-muted)", whiteSpace: "nowrap",
              cursor: c.sortable ? "pointer" : "default", userSelect: "none", textAlign: c.align ?? "left", position: "sticky", top: 0, zIndex: "var(--z-sticky)" as any }}>
            {c.header}{arrow(c)}
          </div>
        ))}
        {rows.map((r) => {
          const exp = expandedRender?.(r.original) ?? null;
          return [
            ...cols.map((c) => (
              <div key={r.id + c.id} onClick={() => onRowClick?.(r.original)}
                onMouseEnter={() => setHoverRow(r.id)} onMouseLeave={() => setHoverRow((h) => (h === r.id ? null : h))}
                style={{ padding: "var(--space-5) var(--cell-px)", borderBottom: exp ? "none" : "1px solid var(--color-border)", textAlign: c.align ?? "left",
                  background: hoverRow === r.id ? "var(--color-surface-hover)" : undefined,
                  fontVariantNumeric: c.align === "right" ? "tabular-nums" : undefined,
                  cursor: onRowClick ? "pointer" : "default", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {c.cell(r.original)}
              </div>
            )),
            exp ? <div key={r.id + "-exp"} style={{ gridColumn: "1 / -1", borderBottom: "1px solid var(--color-border)", background: "var(--color-surface-sunken)", padding: "var(--space-2) var(--space-5) var(--space-6) var(--space-9)" }}>{exp}</div> : null,
          ];
        })}
      </div>
      {rows.length === 0 && (
        <div className="avio-empty">
          <i className="fa-regular fa-folder-open avio-empty__icon" />
          <div className="avio-empty__title">{emptyText}</div>
        </div>
      )}
    </div>
  );
}
